import type { EntityViewGantt } from './entity.view.gantt';
import { FlattenType } from '@jms/model/types';
import { ActivityEntityName } from '../../shared/activity.entity.name';
import { DomainType } from '../../../utilities/domain.utility';
import { Entity } from '../../entity';
import { EntityCalendarOrientation, EntityCalendarResourceType, EntityCalendarView, EntityViewDisplay, EntityViewSortDirection, EntityViewType, EntityViewVisibility } from './enums';
import { DayShortName } from '../../shared/schedule.entity';
import { EntityCalendarResourceGroupBy, EntityViewCalendar, EntityViewCalendarResourceLane, EntityViewCalendarResources } from './entity.view.calendar';
import { EntityAdvancedFilterCondition, EntityAdvancedFilterGroup, EntityAdvancedFilterLogicalOperator, EntityAdvancedFilterNode, EntityAdvancedFilterOperatorForField, EntityAdvancedFilterTargetStrategy, EntityAdvancedFilterValueForField } from './entity.view.advanced.filter';
import { EntityFilterValue, EntityViewFilter, EntityRawViewFilter } from './entity.view.filter';
import { EntityView, EntityViewBoard, EntityViewCard, EntityViewCard2, EntityViewField, EntityViewHighlight, EntityViewKanban, EntityViewKanbanColumn, EntityViewList, EntityViewTable, EntityViewRecordScope, EntityViewSort } from './entity.view';
import { EntityViewGroupHeader } from './entity.view.group.header';

function hashViewId(value: string): string {

    let hash = 2166136261;

    for (let i = 0; i < value.length; i++) {
        hash ^= value.charCodeAt(i);
        hash = Math.imul(hash, 16777619);
    }

    return (hash >>> 0).toString(16).padStart(8, '0');
}

// ── Card Builder ────────────────────────────────────────────────────

class CardBuilder<T = any> {

    private _identifier: FlattenType<T>;
    private _title: FlattenType<T>;
    private _description: FlattenType<T>;
    private _status: FlattenType<T>;
    private _body: FlattenType<T>[] = [];
    private _footer: FlattenType<T>[] = [];
    private _highlight: EntityViewHighlight<T>;

    public identifier(field: FlattenType<T>): this { this._identifier = field; return this; }
    public title(field: FlattenType<T>): this { this._title = field; return this; }
    public description(field: FlattenType<T>): this { this._description = field; return this; }
    public status(field: FlattenType<T>): this { this._status = field; return this; }
    public body(fields: FlattenType<T>[]): this { this._body = fields; return this; }
    public footer(fields: FlattenType<T>[]): this { this._footer = fields; return this; }
    public highlight(field: FlattenType<T>, days: number): this { this._highlight = { _id: field, days }; return this; }

    public build(): EntityViewCard<T> {

        return {
            _id: '',
            name: '',
            identifier: this._identifier ?? ('none' as any),
            title: this._title ?? ('none' as any),
            description: this._description ?? ('none' as any),
            status: this._status ?? ('none' as any),
            body: this._body,
            footer: this._footer,
            highlight: this._highlight ?? { _id: '' as any, days: 0 },
        };
    }
}

// ── Card2 Builder ──────────────────────────────────────────────────

class Card2Builder<T = any> {

    private _sortBy: FlattenType<T>;
    private _sortOrder: EntityViewSortDirection = EntityViewSortDirection.Asc;
    private _cardBuilder: CardBuilder<T>;

    public sortBy(field: FlattenType<T>, direction: EntityViewSortDirection = EntityViewSortDirection.Asc): this { this._sortBy = field; this._sortOrder = direction; return this; }

    public card(fn: (b: CardBuilder<T>) => CardBuilder<T>): this {

        this._cardBuilder = fn(new CardBuilder<T>());
        return this;
    }

    public build(): EntityViewCard2<T> {

        return {
            _id: '',
            card: this._cardBuilder?.build() ?? new CardBuilder<T>().build(),
            sortBy: this._sortBy ?? ('none' as any),
            sortOrder: this._sortOrder,
        };
    }
}

// ── Board Builder ───────────────────────────────────────────────────

class BoardBuilder<T = any> {

    private _column: FlattenType<T>;
    private _row: FlattenType<T>;
    private _sortBy: FlattenType<T>;
    private _sortOrder: EntityViewSortDirection = EntityViewSortDirection.Asc;
    private _cardBuilder: CardBuilder<T>;

    public column(field: FlattenType<T>): this { this._column = field; return this; }
    public row(field: FlattenType<T>): this { this._row = field; return this; }
    public sortBy(field: FlattenType<T>, direction: EntityViewSortDirection = EntityViewSortDirection.Asc): this { this._sortBy = field; this._sortOrder = direction; return this; }

    public card(fn: (b: CardBuilder<T>) => CardBuilder<T>): this {

        this._cardBuilder = fn(new CardBuilder<T>());
        return this;
    }

    public build(): EntityViewBoard<T> {

        return {
            _id: '',
            card: this._cardBuilder?.build() ?? new CardBuilder<T>().build(),
            column: this._column ?? ('none' as any),
            row: this._row ?? ('none' as any),
            sortBy: this._sortBy ?? ('none' as any),
            sortOrder: this._sortOrder,
        };
    }
}

class KanbanBuilder<T = any> {

    private _column: FlattenType<T>;
    private _row: FlattenType<T>;
    private _columns: EntityViewKanbanColumn[] = [];
    private _rowHeader: EntityViewGroupHeader;
    private _sortBy: FlattenType<T>;
    private _sortOrder: EntityViewSortDirection = EntityViewSortDirection.Asc;
    private _cardBuilder: CardBuilder<T>;

    public column(field: FlattenType<T>): this { this._column = field; return this; }
    public row(field: FlattenType<T>): this { this._row = field; return this; }
    /** Swimlane header chrome for the row field; omit to keep the row field's metadata groupHeaderDefaults. */
    public rowHeader(header: EntityViewGroupHeader): this { this._rowHeader = header; return this; }
    /** Ordered column definitions; omit entirely to keep following the default per-value generator. */
    public columns(columns: EntityViewKanbanColumn[]): this { this._columns = columns ?? []; return this; }
    public sortBy(field: FlattenType<T>, direction: EntityViewSortDirection = EntityViewSortDirection.Asc): this { this._sortBy = field; this._sortOrder = direction; return this; }

    public card(fn: (b: CardBuilder<T>) => CardBuilder<T>): this {

        this._cardBuilder = fn(new CardBuilder<T>());
        return this;
    }

    public build(): EntityViewKanban<T> {

        return {
            _id: '',
            card: this._cardBuilder?.build() ?? new CardBuilder<T>().build(),
            column: this._column ?? ('none' as any),
            row: this._row ?? ('none' as any),
            columns: this._columns.length ? this._columns : undefined,
            rowHeader: this._rowHeader,
            sortBy: this._sortBy ?? ('none' as any),
            sortOrder: this._sortOrder,
        };
    }
}

// ── Calendar Builder ────────────────────────────────────────────────

class CalendarResourcesBuilder<T = any> {

    private _field: FlattenType<T>;
    private _lanes: Partial<Record<EntityCalendarResourceType, EntityViewCalendarResourceLane>> = {};
    private _groupBy: EntityCalendarResourceGroupBy;
    private _selection: EntityViewCalendarResources['selection'];
    private _mineOnly: boolean;
    private _showAvatar: boolean;
    private _showSuburb: boolean;
    private _showSchedulingTags: boolean;
    private _showTimezone: boolean;
    private _showType: boolean;

    public field(field: FlattenType<T>): this { this._field = field; return this; }
    public lane(type: EntityCalendarResourceType, restrictTo?: Entity[]): this { this._lanes[type] = { show: true, ...(restrictTo?.length ? { restrictTo } : {}) }; return this; }
    /** Nest lanes under a parent per distinct value of this attribute of the resource ref. */
    public groupBy(groupBy: EntityCalendarResourceGroupBy): this { this._groupBy = groupBy; return this; }
    public namedResources(ids: string[]): this { this._selection = { mode: 'named', ids }; return this; }
    public teams(ids: string[]): this { this._selection = { mode: 'teams', ids }; return this; }
    public mineOnly(value = true): this { this._mineOnly = value; return this; }
    public showAvatar(value = true): this { this._showAvatar = value; return this; }
    public showSuburb(value = true): this { this._showSuburb = value; return this; }
    public showSchedulingTags(value = true): this { this._showSchedulingTags = value; return this; }
    public showTimezone(value = true): this { this._showTimezone = value; return this; }
    public showType(value = true): this { this._showType = value; return this; }

    public build(): EntityViewCalendarResources<T> {

        return {
            field: this._field,
            lanes: this._lanes,
            ...(this._selection ? { selection: this._selection } : {}),
            ...(this._groupBy !== undefined ? { groupBy: this._groupBy } : {}),
            ...(this._mineOnly !== undefined ? { mineOnly: this._mineOnly } : {}),
            ...(this._showAvatar !== undefined ? { showAvatar: this._showAvatar } : {}),
            ...(this._showSuburb !== undefined ? { showSuburb: this._showSuburb } : {}),
            ...(this._showSchedulingTags !== undefined ? { showSchedulingTags: this._showSchedulingTags } : {}),
            ...(this._showTimezone !== undefined ? { showTimezone: this._showTimezone } : {}),
            ...(this._showType !== undefined ? { showType: this._showType } : {}),
        };
    }
}

class CalendarBuilder<T = any> {

    private _view: EntityCalendarView = EntityCalendarView.DayGridMonth;
    private _views: EntityCalendarView[];
    private _startField: FlattenType<T>;
    private _endField: FlattenType<T>;
    private _allDay = true;
    private _cardBuilder: CardBuilder<T>;
    private _colorField: FlattenType<T>;
    private _resourcesBuilder: CalendarResourcesBuilder<T>;
    private _showDays: number;
    private _daysOfWeek: DayShortName[];
    private _slotMinTime: string;
    private _slotMaxTime: string;
    private _slotDuration: number;
    private _timezone: string;
    private _orientation: EntityCalendarOrientation;
    private _autoRefresh: number;

    public view(view: EntityCalendarView): this { this._view = view; return this; }
    public views(views: EntityCalendarView[]): this { this._views = views; return this; }
    /** Event window. Omit `end` for point / all-day events. */
    public range(start: FlattenType<T>, end?: FlattenType<T>): this { this._startField = start; this._endField = end; return this; }
    public allDay(value = true): this { this._allDay = value; return this; }
    public timed(): this { this._allDay = false; return this; }
    public colorBy(field: FlattenType<T>): this { this._colorField = field; return this; }
    public showDays(days: number): this { this._showDays = days; return this; }
    public daysOfWeek(days: DayShortName[]): this { this._daysOfWeek = days; return this; }
    /** `HH:mm` bounds. */
    public visibleHours(min: string, max: string): this { this._slotMinTime = min; this._slotMaxTime = max; return this; }
    public slotDuration(minutes: number): this { this._slotDuration = minutes; return this; }
    public timezone(timezone: string): this { this._timezone = timezone; return this; }
    public orientation(orientation: EntityCalendarOrientation): this { this._orientation = orientation; return this; }
    public autoRefresh(minutes: number): this { this._autoRefresh = minutes; return this; }

    /** Event card slots, mirroring `KanbanBuilder.card` so one card shape configures every display. */
    public card(fn: (b: CardBuilder<T>) => CardBuilder<T>): this {

        this._cardBuilder = fn(new CardBuilder<T>());
        return this;
    }

    public resources(fn: (b: CalendarResourcesBuilder<T>) => CalendarResourcesBuilder<T>): this {

        this._resourcesBuilder = fn(new CalendarResourcesBuilder<T>());
        return this;
    }

    public build(): EntityViewCalendar<T> {

        return {
            _id: '',
            view: this._view,
            ...(this._views?.length ? { views: this._views } : {}),
            startField: this._startField,
            ...(this._endField !== undefined ? { endField: this._endField } : {}),
            allDay: this._allDay,
            card: this._cardBuilder?.build() ?? new CardBuilder<T>().build(),
            ...(this._colorField !== undefined ? { colorField: this._colorField } : {}),
            ...(this._resourcesBuilder ? { resources: this._resourcesBuilder.build() } : {}),
            ...(this._showDays !== undefined ? { showDays: this._showDays } : {}),
            ...(this._daysOfWeek?.length ? { daysOfWeek: this._daysOfWeek } : {}),
            ...(this._slotMinTime !== undefined ? { slotMinTime: this._slotMinTime } : {}),
            ...(this._slotMaxTime !== undefined ? { slotMaxTime: this._slotMaxTime } : {}),
            ...(this._slotDuration !== undefined ? { slotDuration: this._slotDuration } : {}),
            ...(this._timezone !== undefined ? { timezone: this._timezone } : {}),
            ...(this._orientation !== undefined ? { orientation: this._orientation } : {}),
            ...(this._autoRefresh !== undefined ? { autoRefresh: this._autoRefresh } : {}),
        };
    }
}

// ── Table Builder ───────────────────────────────────────────────────

class TableBuilder<T = any> {

    private _fixed: FlattenType<T>[] = [];
    private _other: FlattenType<T>[] = [];
    private _groupBy: FlattenType<T>;
    private _subGroupBy: FlattenType<T>;
    private _highlight: EntityViewHighlight<T>;

    public fixed(fields: FlattenType<T>[]): this { this._fixed = fields; return this; }
    public columns(fields: FlattenType<T>[]): this { this._other = fields; return this; }
    public groupBy(field: FlattenType<T>): this { this._groupBy = field; return this; }
    public subGroupBy(field: FlattenType<T>): this { this._subGroupBy = field; return this; }
    public highlight(field: FlattenType<T>, days: number): this { this._highlight = { _id: field, days }; return this; }

    private toViewFields(fields: FlattenType<T>[]): EntityViewField<T>[] {

        return fields.filter(f => !!f).map(f => ({ _id: f, name: String(f), systemHidden: false, visible: true }));
    }

    public build(): EntityViewTable<T> {

        return {
            _id: '',
            fixed: this.toViewFields(this._fixed),
            other: this.toViewFields(this._other),
            highlight: this._highlight ?? { _id: '' as any, days: 0 },
            groupBy: this._groupBy,
            subGroupBy: this._subGroupBy,
        };
    }
}

// ── List Builder ───────────────────────────────────────────────────

class ListBuilder<T = any> {

    private _identifier: FlattenType<T>;
    private _title: FlattenType<T>;
    private _status: FlattenType<T>;
    private _fields: FlattenType<T>[] = [];
    private _groupBy: FlattenType<T>;
    private _subGroupBy: FlattenType<T>;
    private _sortBy: FlattenType<T>;
    private _sortOrder: EntityViewSortDirection = EntityViewSortDirection.Asc;

    public identifier(field: FlattenType<T>): this { this._identifier = field; return this; }
    public title(field: FlattenType<T>): this { this._title = field; return this; }
    public status(field: FlattenType<T>): this { this._status = field; return this; }
    public fields(fields: FlattenType<T>[]): this { this._fields = fields; return this; }
    public groupBy(field: FlattenType<T>): this { this._groupBy = field; return this; }
    public subGroupBy(field: FlattenType<T>): this { this._subGroupBy = field; return this; }
    public sortBy(field: FlattenType<T>, direction: EntityViewSortDirection = EntityViewSortDirection.Asc): this { this._sortBy = field; this._sortOrder = direction; return this; }

    public build(): EntityViewList<T> {

        return {
            _id: '',
            identifier: this._identifier ?? ('none' as any),
            title: this._title ?? ('none' as any),
            status: this._status ?? ('none' as any),
            fields: this._fields,
            groupBy: this._groupBy,
            subGroupBy: this._subGroupBy,
            sortBy: this._sortBy ?? ('none' as any),
            sortOrder: this._sortOrder,
        };
    }
}

// ── Advanced Filter Builder ─────────────────────────────────────────

export class AdvancedFilterBuilder<T = any> {

    public condition<K extends FlattenType<T>>(fieldId: K, operator: EntityAdvancedFilterOperatorForField<T, K>, value?: EntityAdvancedFilterValueForField<T, K>): EntityAdvancedFilterCondition<T, K> {

        return { fieldId, operator, value } as any;
    }

    public and(...conditions: EntityAdvancedFilterCondition<T>[]): EntityAdvancedFilterGroup<T> {

        return { operator: EntityAdvancedFilterLogicalOperator.And, rules: conditions };
    }

    public or(...conditions: EntityAdvancedFilterCondition<T>[]): EntityAdvancedFilterGroup<T> {

        return { operator: EntityAdvancedFilterLogicalOperator.Or, rules: conditions };
    }
}

// ── View Builder ────────────────────────────────────────────────────

export class EntityViewBuilder<T = any> {

    private _id: string;
    private _name: string;
    private _entity: ActivityEntityName;
    private _domain: DomainType;
    private _type: EntityViewType = EntityViewType.System;
    private _display: EntityViewDisplay = EntityViewDisplay.Table;
    private _pageSize: number;
    private _recordScope: EntityViewRecordScope;
    private _visibility: EntityViewVisibility = EntityViewVisibility.Public;
    private _variant: string;

    private _filters: EntityViewFilter<T>[] = [];
    private _hiddenFilters: FlattenType<T>[] = [];
    private _rawFilters: EntityRawViewFilter<T>[] = [];
    private _quickFilters: FlattenType<T>[] = [];
    private _advancedFilter: EntityAdvancedFilterGroup<T>;
    private _advancedFilterStrategy: EntityAdvancedFilterTargetStrategy;

    private _sort: EntityViewSort<T>;
    private _card2Builder: Card2Builder<T>;
    private _boardBuilder: BoardBuilder<T>;
    private _kanbanBuilder: KanbanBuilder<T>;
    private _gantt: EntityViewGantt;
    private _calendarBuilder: CalendarBuilder<T>;
    private _listBuilder: ListBuilder<T>;
    private _tableBuilder: TableBuilder<T>;

    constructor(entity: ActivityEntityName, domain: DomainType = DomainType.Tenant) {

        this._entity = entity;
        this._domain = domain;
    }

    /** Clone an existing view into a new builder for small modifications */
    public static from<T = any>(view: Partial<EntityView<T>>): EntityViewBuilder<T> {

        const b = new EntityViewBuilder<T>(view.entity, view.domain);
        b._id = view._id;
        b._name = view.name;
        b._type = view.type;
        b._display = view.display;
        b._pageSize = view.pageSize;
        b._recordScope = view.recordScope;
        b._visibility = view.visibility;
        b._variant = view.variant;
        b._filters = [...(view.filters ?? [])];
        b._hiddenFilters = [...(view.hiddenFilters ?? [])];
        b._rawFilters = [...(view.rawFilters ?? [])];
        b._quickFilters = [...(view.quickFilters ?? [])];
        b._sort = view.sort ? { ...view.sort } : undefined;
        b._gantt = view.gantt ? { ...view.gantt } : undefined;
        b._advancedFilter = view.advancedFilter;
        b._advancedFilterStrategy = view.advancedFilterStrategy;
        return b;
    }

    /** Clone this builder's current state into a new builder */
    public clone(): EntityViewBuilder<T> {

        const b = new EntityViewBuilder<T>(this._entity, this._domain);
        b._id = this._id;
        b._name = this._name;
        b._type = this._type;
        b._display = this._display;
        b._pageSize = this._pageSize;
        b._recordScope = this._recordScope;
        b._visibility = this._visibility;
        b._variant = this._variant;
        b._filters = [...this._filters];
        b._hiddenFilters = [...this._hiddenFilters];
        b._rawFilters = [...this._rawFilters];
        b._quickFilters = [...this._quickFilters];
        b._sort = this._sort ? { ...this._sort } : undefined;
        b._advancedFilter = this._advancedFilter;
        b._advancedFilterStrategy = this._advancedFilterStrategy;
        b._card2Builder = this._card2Builder;
        b._boardBuilder = this._boardBuilder;
        b._kanbanBuilder = this._kanbanBuilder;
        b._gantt = this._gantt ? { ...this._gantt } : undefined;
        b._calendarBuilder = this._calendarBuilder;
        b._listBuilder = this._listBuilder;
        b._tableBuilder = this._tableBuilder;
        return b;
    }

    // ── Identity ──────────────────────────────────────────────────────

    public id(id: string): this { this._id = id; return this; }
    public name(name: string): this { this._name = name; return this; }
    public domain(domain: DomainType): this { this._domain = domain; return this; }
    public variant(variant: string): this { this._variant = variant; return this; }

    // ── Type & Display ────────────────────────────────────────────────

    public system(): this { this._type = EntityViewType.System; return this; }
    public user(): this { this._type = EntityViewType.User; return this; }
    public embedded(): this { this._type = EntityViewType.Embedded; return this; }
    public template(): this { this._type = EntityViewType.Template; return this; }
    public display(display: EntityViewDisplay): this { this._display = display; return this; }
    public pageSize(pageSize: number): this { this._pageSize = pageSize; return this; }
    public recordScope(recordScope: EntityViewRecordScope): this { this._recordScope = recordScope; return this; }
    public visibility(visibility: EntityViewVisibility): this { this._visibility = visibility; return this; }

    // ── Filters ───────────────────────────────────────────────────────

    public filter(field: FlattenType<T>, value: EntityFilterValue): this {

        this._filters.push({ _id: field, value });
        return this;
    }

    /** Hide a filter field from the UI (field still available for system use) */
    public hiddenFilter(field: FlattenType<T>): this {

        this._hiddenFilters.push(field);
        return this;
    }

    public rawFilter(field: FlattenType<T>, value: any): this {

        this._rawFilters.push({ _id: field, value });
        return this;
    }

    public quickFilters(fields: FlattenType<T>[]): this { this._quickFilters = fields; return this; }

    public advancedFilter(fn: (f: AdvancedFilterBuilder<T>) => EntityAdvancedFilterGroup<T>, strategy?: EntityAdvancedFilterTargetStrategy): this {

        this._advancedFilter = fn(new AdvancedFilterBuilder<T>());
        this._advancedFilterStrategy = strategy;
        return this;
    }

    // ── Sorting ───────────────────────────────────────────────────────

    public sort(field: FlattenType<T>, direction: EntityViewSortDirection = EntityViewSortDirection.Asc): this {

        this._sort = { _id: field, direction };
        return this;
    }

    // ── Card, Board & Table ────────────────────────────────────────────

    public card2(fn: (b: Card2Builder<T>) => Card2Builder<T>): this {

        this._card2Builder = fn(new Card2Builder<T>());
        return this;
    }

    public board(fn: (b: BoardBuilder<T>) => BoardBuilder<T>): this {

        this._boardBuilder = fn(new BoardBuilder<T>());
        return this;
    }

    public kanban(fn: (b: KanbanBuilder<T>) => KanbanBuilder<T>): this {

        this._kanbanBuilder = fn(new KanbanBuilder<T>());
        return this;
    }

    public gantt(config: EntityViewGantt): this {

        this._gantt = { ...config };
        return this;
    }

    public calendar(fn: (b: CalendarBuilder<T>) => CalendarBuilder<T>): this {

        this._calendarBuilder = fn(new CalendarBuilder<T>());
        return this;
    }

    public list(fn: (b: ListBuilder<T>) => ListBuilder<T>): this {

        this._listBuilder = fn(new ListBuilder<T>());
        return this;
    }

    public table(fn: (b: TableBuilder<T>) => TableBuilder<T>): this {

        this._tableBuilder = fn(new TableBuilder<T>());
        return this;
    }

    // ── Build ─────────────────────────────────────────────────────────

    public build(): Partial<EntityView<T>> {

        const defaultId = this._type === EntityViewType.System
            ? `${this._entity}-${hashViewId(`${this._entity}|${this._name}|${this._domain ?? DomainType.Tenant}|${this._variant ?? ''}`)}`
            : `${this._entity}-${this._name}`;

        return {
            _id: this._id ?? defaultId,
            name: this._name,
            entity: this._entity,
            domain: this._domain,
            type: this._type,
            display: this._display,
            pageSize: this._pageSize,
            recordScope: this._recordScope,
            visibility: this._visibility,
            variant: this._variant,
            system: this._type === EntityViewType.System,
            filters: this._filters,
            hiddenFilters: this._hiddenFilters.length ? this._hiddenFilters : undefined,
            rawFilters: this._rawFilters.length ? this._rawFilters : undefined,
            quickFilters: this._quickFilters,
            advancedFilter: this._advancedFilter,
            advancedFilterStrategy: this._advancedFilterStrategy,
            sort: this._sort ?? { _id: 'createdAt' as any, direction: EntityViewSortDirection.Desc },
            card2: this._card2Builder?.build(),
            board: this._boardBuilder?.build(),
            kanban: this._kanbanBuilder?.build(),
            calendar: this._calendarBuilder?.build(),
            gantt: this._gantt,
            list: this._listBuilder?.build(),
            table: this._tableBuilder?.build(),
        } as any;
    }
}
